'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CalculatorInputs from './CalculatorInputs';
import ResultsDisplay from './ResultsDisplay';
import CompareChart from './CompareChart';
import { CompareFundReturnValues, getNumberLocale } from './types';
import { compareFundRobots, generateChartData } from './calculations';
import { ToolsTranslations } from '@/lib/translations/types';
import { useToolTracking } from '@/lib/analytics';

interface Props {
  translations: ToolsTranslations['compareFundReturn'];
}

export default function CompareFundReturnCalculator({ translations }: Props) {
  const locale = getNumberLocale();
  const { trackUsage } = useToolTracking('compareFundReturn');
  const hasTrackedUsage = useRef(false);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [values, setValues] = useState<CompareFundReturnValues>({
    monthlySavings: 2000,
    startingAmount: 10000,
    years: 15,
    mode: 'simple',
    robotAReturn: 7,
    robotAFee: 0.4,
    robotBReturn: 7,
    robotBFee: 0.89,
  });

  const debouncedTrackUsage = useCallback(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (!hasTrackedUsage.current) {
      debounceTimeout.current = setTimeout(() => {
        trackUsage();
        hasTrackedUsage.current = true;
      }, 2000);
    }
  }, [trackUsage]);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleValuesChange = (
    field: keyof CompareFundReturnValues,
    value: number | CompareFundReturnValues['mode']
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    debouncedTrackUsage();
  };

  const result = useMemo(() => compareFundRobots(values, translations), [values, translations]);
  const chartData = useMemo(() => generateChartData(values, translations), [values, translations]);

  return (
    <section id="compare-fund-return" className="w-full my-12 tools">
      <div className="rounded-xl border border-gray-200 shadow-sm bg-white">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-blue-900">{translations.title}</h2>
          <p className="text-sm text-gray-600 mt-1">{translations.subtitle}</p>
        </div>

        <div className="p-6 space-y-8">
          <CalculatorInputs
            values={values}
            onValuesChange={handleValuesChange}
            translations={translations}
            locale={locale}
          />

          <ResultsDisplay
            result={result}
            translations={translations}
            locale={locale}
            mode={values.mode}
          />

          {result.isValid && (
            <CompareChart chartData={chartData} translations={translations} locale={locale} />
          )}

          <p className="text-sm text-gray-500">{translations.disclaimer}</p>
        </div>
      </div>
    </section>
  );
}
